import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { FaSave } from 'react-icons/fa'
import { downloadImage, isUrl, saveImage } from '../helpers/helpers'

const useIntersectionObserver = () => {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting)
        })
      },
      { threshold: 0.1 },
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [])

  return [ref, isVisible]
}

const ImageGridItem = ({ img, user, blurryImage, index }) => {
  const [ref, isVisible] = useIntersectionObserver()

  const handleSave = (e) => {
    e.preventDefault()
    e.stopPropagation()

    // Same logic pattern as Create.jsx: if it's a URL -> download, else -> save base64
    if (typeof img === 'string' && isUrl(img)) {
      downloadImage(img)
    } else {
      saveImage(`data:image/jpeg;base64,${img}`, `image_${index}.jpg`)
    }
  }

  return (
    <div className="relative" ref={ref}>
      {/* Save/Download icon */}
      {img ? (
        <button
          type="button"
          aria-label="Save file"
          title="Save file"
          className="absolute top-2 right-2 z-10 flex items-center justify-center h-9 w-9 rounded-lg bg-[#0b0a0f]/80 hover:bg-[#0b0a0f] text-white"
          onClick={handleSave}
        >
          <FaSave className="text-base" />
        </button>
      ) : null}

      {isVisible ? (
        typeof img === 'string' && img.startsWith('http') ? (
          <Link href={`/profile/${user.uid}`}>
            <img
              key={img}
              src={img.replace(
                process.env.NEXT_PUBLIC_URL_1,
                process.env.NEXT_PUBLIC_URL_2,
              )}
              alt="Generated"
              className="object-cover rounded-lg h-64 w-64 fade-in-out border-2 border-[var(--main-bg-color)]"
            />
          </Link>
        ) : (
          <img
            key={img}
            src={`data:image/jpeg;base64,${img}`}
            alt="Generated"
            className={`object-cover rounded-lg h-64 w-64 fade-in-out border-2 border-orange-300 ${
              blurryImage ? 'blur-md' : ''
            }`}
          />
        )
      ) : (
        <div className="w-64 h-64 bg-gray-200 rounded-lg"></div>
      )}
    </div>
  )
}

const ImageGrid = ({ imageHistory, user, blurryImage }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-12 flex-wrap self-center items-center">
      {[...imageHistory]
        .reverse()
        .map((img, index) =>
          img ? (
            <ImageGridItem
              key={index}
              img={img}
              user={user}
              blurryImage={blurryImage}
              index={index}
            />
          ) : null,
        )}
    </div>
  )
}

export default ImageGrid
